import { useTranslate } from 'react-polyglot';

import { RoundedButton } from '@genially/design-system';

import type { SessionAnalyticsQuestion } from '../../../../utils/buildSessionAnalytics';
import { getQuestionText } from '../../../../utils/getQuestionText';
import { EyeIcon } from './EyeIcon';
import {
  AverageScale,
  AverageValue,
  BarFill,
  BarTrack,
  GoToSlideButton,
  InfoText,
  OpenAnswerCount,
  OptionLabel,
  QuestionTitle,
  Wrapper,
} from './ResolvedQuestionRow.styled';

interface ResolvedQuestionRowProps {
  entry: SessionAnalyticsQuestion;
  onGoToSlide: (slideId: string) => void;
}

export const ResolvedQuestionRow = ({ entry, onGoToSlide }: ResolvedQuestionRowProps) => {
  const t = useTranslate();

  const { question, isRandom, responses, chart } = entry;

  const title = getQuestionText(question, isRandom, t);

  const chartContent = (() => {
    switch (chart.kind) {
      case 'hitRate':
        return (
          <>
            <BarTrack>
              <BarFill $percentage={chart.percentage} />
            </BarTrack>
            <InfoText>
              {t('__new.play.analyticsResults.info.hitRate', {
                responses,
                percentage: chart.percentage,
              })}
            </InfoText>
          </>
        );

      case 'topOption':
        return (
          <>
            <OptionLabel>
              {chart.label ??
                t('__new.play.analyticsResults.option.unnamed', {
                  position: chart.optionPosition,
                })}
            </OptionLabel>
            <InfoText>
              {t('__new.play.analyticsResults.info.topOption', {
                responses,
                percentage: chart.percentage,
              })}
            </InfoText>
          </>
        );

      case 'average':
        return (
          <>
            <AverageValue>
              {chart.average}
              <AverageScale>{` / ${chart.scale}`}</AverageScale>
            </AverageValue>
            <InfoText>
              {t('__new.play.analyticsResults.info.average', { responses })}
            </InfoText>
          </>
        );

      default:
        return (
          <OpenAnswerCount>
            {t('__new.play.analyticsResults.info.openAnswer', { responses })}
          </OpenAnswerCount>
        );
    }
  })();

  return (
    <Wrapper>
      <GoToSlideButton>
        <RoundedButton
          Icon={<EyeIcon />}
          variant={RoundedButton.Variant.PRIMARY}
          size={RoundedButton.Size.SMALL}
          onClick={() => onGoToSlide(question.IdSlide)}
          aria-label={t('__new.play.analyticsResults.question.goToSlide')}
        />
      </GoToSlideButton>

      <QuestionTitle>{title}</QuestionTitle>
      {chartContent}
    </Wrapper>
  );
};
